


















































































import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';

import UiCheckbox from './UiCheckbox.vue';

export interface DropdownItemOptions {
  value: unknown;
  label: string;
  icon?: string;
  disabled?: boolean;
}

@Component({ components: { UiCheckbox } })
export default class UiDropdown extends Vue {
  @Prop()
  public readonly label?: string;

  @Prop()
  public readonly value?: unknown[] | unknown;

  @Prop({ required: true })
  public readonly values!: (string | DropdownItemOptions)[] | Record<string, unknown>;

  @Prop({ default: 'left' })
  public readonly align!: 'left' | 'right';

  @Prop({ default: false })
  public readonly hasCheckBox!: boolean;

  @Prop({ default: false })
  public readonly up!: boolean;

  public showMenu = false;
  private blurTimerId?: ReturnType<typeof setTimeout>;

  // Type-ahead state: the currently keyboard-highlighted option's value, plus
  // the last typed letter and its matched index (for repeated-press cycling).
  // NOTE: initialized to null, not undefined — a vue-class-component data
  // property initialized to `undefined` is NOT made reactive (Vue 2 can't
  // define the getter/setter), so the highlight class wouldn't update.
  public highlightedValue: unknown = null;
  private typeaheadKey = '';
  private typeaheadPos = -1;

  $refs!: {
    menuList: any;
  };

  public get normalValues(): (DropdownItemOptions & { selected: boolean })[] {
    return (
      Array.isArray(this.values)
        ? this.values.map((x) => (typeof x === 'string' ? { value: x, label: x } : x))
        : Object.entries(this.values).map(([label, value]) => ({ label, value }))
    ).map((x) => ({ ...x, selected: (Array.isArray(this.value) && this.value?.includes(x.value)) ?? false }));
  }

  public onSelect(value: DropdownItemOptions) {
    if (value.disabled) {
      return;
    }
    this.$emit('select', value.value);
  }

  public onFocus() {
    if (this.blurTimerId) {
      clearTimeout(this.blurTimerId);
      this.blurTimerId = undefined;
    }
    this.showMenu = true;
  }

  public onBlur() {
    if (this.blurTimerId) {
      return;
    }
    this.blurTimerId = setTimeout(() => {
      if (this.blurTimerId) {
        this.showMenu = false;
        clearTimeout(this.blurTimerId);
        this.blurTimerId = undefined;
      }
    }, 50);
  }

  public onClickRow(item: DropdownItemOptions & { selected: boolean }) {
    let selectedItems = this.normalValues
      .filter((x: DropdownItemOptions & { selected: boolean }) => x.selected)
      .map((x: DropdownItemOptions & { selected: boolean }) => x.value);
    if (item.selected) {
      selectedItems = selectedItems.filter((el: unknown) => el !== item.value);
    } else {
      selectedItems.push(item.value);
    }
    this.$emit('input', selectedItems);
  }

  // onKeydown implements keyboard type-ahead for single-select dropdowns:
  // pressing a printable key jumps to (and highlights) the first option whose
  // label starts with that character; pressing the same key again cycles to the
  // next match; Enter selects the highlighted option. No-op for checkbox
  // (multi-select) dropdowns. Mouse selection is unaffected.
  public onKeydown(e: KeyboardEvent) {
    if (!this.showMenu || this.hasCheckBox) {
      return;
    }
    if (e.key === 'Enter') {
      const item = this.normalValues.find((x) => x.value === this.highlightedValue);
      if (item && !item.disabled) {
        e.preventDefault();
        this.onSelect(item);
        this.showMenu = false;
      }
      return;
    }
    if (e.key.length !== 1 || !/\S/.test(e.key) || e.metaKey || e.ctrlKey || e.altKey) {
      return;
    }
    const lower = e.key.toLowerCase();
    const matches = this.normalValues
      .map((x, i) => ({ x, i }))
      .filter(({ x }) => !x.disabled && (x.label ?? '').toLowerCase().startsWith(lower));
    if (matches.length === 0) {
      return;
    }
    e.preventDefault();
    let pick = matches[0];
    if (this.typeaheadKey === lower) {
      // Same letter pressed again — advance to the next match (wrap around).
      pick = matches.find((m) => m.i > this.typeaheadPos) ?? matches[0];
    }
    this.typeaheadKey = lower;
    this.typeaheadPos = pick.i;
    this.highlightedValue = pick.x.value;
    this.scrollToIndex(pick.i);
  }

  private scrollToIndex(index: number) {
    this.$nextTick(() => {
      const el = this.$refs.menuList?.children?.[index];
      if (el) {
        el.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  @Watch('showMenu')
  onMenuOpenChanged(newVal?: boolean) {
    this.$emit('open', newVal);
    if (newVal === true && !Array.isArray(this.value)) {
      // Start the highlight on the current selection so type-ahead/Enter work
      // immediately, and scroll it into view.
      this.highlightedValue = this.value;
      this.typeaheadKey = '';
      const currentIndex = this.normalValues.findIndex((el) => el.value === this.value);
      this.typeaheadPos = currentIndex;
      if (currentIndex >= 0) {
        this.$nextTick(() => {
          const el = this.$refs.menuList.children[currentIndex];
          if (el) {
            el.scrollIntoView({ block: 'center' });
          }
        });
      }
    } else if (newVal === false) {
      this.highlightedValue = null;
      this.typeaheadKey = '';
      this.typeaheadPos = -1;
    }
  }
}
